import "./tmms-dashboard.scss";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    APP_BASE_URL,
    GET_ALL_ASSETS,
    GET_ALL_COUNTS_TMMS_DASHBOARD,
    GET_ASSET_HEALTH,
    GET_MAINTENANCE_SCHEDULE,
} from "../../Common/EndPoints";
import axios from "axios";
import AssetWidget from "./AssetWidget";
import MaintenanceScheduleWidget from "./MaintenanceScheduleWidget";
import AssetHealthWidget from "./AssetHealthWidget";
import { useDispatch, useSelector } from "react-redux";
import { selectedCustomersSelector } from "../../features/authentication/auth-slice";
import { Autocomplete, Button, Chip, TextField } from "@mui/material";
import { AutocompleteStationListPop } from "../AutocompleteStationListPop/AutocompleteStationListPop";
import { useImmer } from "use-immer";
import { getParsedLocalStorage } from "../../Utils/storage-utils";
import { extractParsedDataFromPermissions } from "../../Utils/Utils";
import toast from "react-hot-toast";
import { updateSelectedSidebarKey } from "../NewSidebar/sidebarSlice";

enum TMMSDashboardCard {
    AllAssets = "all-assets",
    MaintenanceSchedule = "maintenance-schedule-started",
    AssetHealthOutsidePermissibleRange = "asset-health-outside-permissible-range",
}

const initialState = {
    timeStamp: 1745217654944,
    AllAssetTotalCount: 0,
    MaintenanceStartedDevices: 0,
    AssetHeathCriticalCount: 0,
};

const getInitFilterFrom = () => {
    let zone_ids: any = [];
    let div_ids: any = [];
    let customer_ids: any = [];
    return {
        zone_ids,
        div_ids,
        customer_ids,
    };
};
const getStrIdListFromOptions = (dd: any = []) => {
    return dd.map((obj: any) => obj.id).join(",");
};
const TMMSDashboard = () => {
    const dispatch = useDispatch()
    const user_permissions = getParsedLocalStorage("permissions");
    const parsedPermissions = useMemo(() => {
        return extractParsedDataFromPermissions(user_permissions);
    }, []);
    const selectedCustomersList = useSelector(selectedCustomersSelector);
    const permittedCustomerIds = useMemo(() => {
        const _list = selectedCustomersList;
        if (!_list) return [];
        return _list.map((customer) => customer.customerId);
    }, [selectedCustomersList]);
    const [filterForm, setFilterForm] = useImmer(() => getInitFilterFrom());

    const [finalFilters, setFinalFilters] = useState<any>();
    const [activeCard, setActiveCard] = useState<TMMSDashboardCard>(TMMSDashboardCard.AllAssets);
    const [allCountsData, setAllCountsData] = useState(initialState);
    const navigate = useNavigate();

    const zone_dd = useMemo(() => {
        return parsedPermissions.zones;
    }, []);
    const div_dd = useMemo(() => {
        let total_divs = parsedPermissions.divisions;
        if (filterForm.zone_ids.length) {
            const ids = filterForm.zone_ids.map((obj: any) => obj.id);
            total_divs = parsedPermissions.divisions.filter((div_obj) => ids.includes(div_obj.zone.id));
            const filtered_form_div_ids = filterForm.div_ids.filter((selected_div_obj: any) => {
                return total_divs.find((obj) => obj.id === selected_div_obj.id);
            });
            setFilterForm((prev) => {
                prev.div_ids = filtered_form_div_ids;
            });
        }

        return total_divs;
    }, [filterForm.zone_ids]);

    const customer_dd = useMemo(() => {
        let filtered_stations = parsedPermissions.stations.filter((obj) => permittedCustomerIds.includes(obj.id));
        if (filterForm.div_ids.length) {
            const selected_div_ids = filterForm.div_ids.map((obj: any) => obj.id);
            filtered_stations = filtered_stations.filter((stationObj) => selected_div_ids.includes(stationObj.division.id));
        } else if (filterForm.zone_ids.length) {
            const selected_zone_ids = filterForm.zone_ids.map((obj: any) => obj.id);
            filtered_stations = filtered_stations.filter((stationObj) => selected_zone_ids.includes(stationObj.zone.id));
        }
        const filtered_form_stn_ids = filterForm.customer_ids.filter((selected_stn_obj: any) => {
            return filtered_stations.find((obj) => obj.id === selected_stn_obj.id);
        });
        setFilterForm((prev) => {
            prev.customer_ids = filtered_form_stn_ids;
        });
        return filtered_stations;
    }, [permittedCustomerIds, filterForm.div_ids, filterForm.zone_ids]);

    const handleFilterChange = (filter_key: any, filter_value: any) => {
        setFilterForm((prev: any) => {
            prev[filter_key] = filter_value;
        });
    };

    const selectedStations = useMemo(() => {
        return filterForm.customer_ids.length
            ? getStrIdListFromOptions(filterForm.customer_ids)
            : permittedCustomerIds.join(",");
    }, [finalFilters, permittedCustomerIds]);

    useEffect(() => {
        const fetchAllCountsData = async () => {
            try {
                const params = {
                    station: filterForm.customer_ids.length
                        ? getStrIdListFromOptions(filterForm.customer_ids)
                        : permittedCustomerIds.join(","),
                };
                const url = `${APP_BASE_URL}${GET_ALL_COUNTS_TMMS_DASHBOARD}`;
                const res = await axios.get(url, { params });
                setAllCountsData(res.data?.data);
            } catch (error: any) {
                if (axios.isAxiosError(error)) {
                    toast.error(error?.response?.data?.message || "Something went wrong");
                } else {
                    toast.error(error?.message || "Something went wrong");
                }
                console.log("ALL_COUNTS_ERROR", error);
            }
        };
        fetchAllCountsData();
    }, [permittedCustomerIds, finalFilters]);

    return (
        <div className="tmms-dashboard">
            <div className="tmms-dashboard-filters app--custome--filter-container">
                <Autocomplete
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    multiple
                    className="filter-form-item"
                    options={zone_dd ?? []}
                    value={filterForm.zone_ids || []}
                    onChange={(_, newValue) => {
                        handleFilterChange("zone_ids", newValue);
                    }}
                    disableCloseOnSelect
                    getOptionLabel={(opt) => opt.value}
                    renderInput={(params) => <TextField {...params} label="Zones" />}
                    renderTags={(value, getTagProps) => {
                        return (
                            <div>
                                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                                {value.length > 1 && (
                                    <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                                        <Chip size="small" label={`+${value.length - 1}`} />
                                    </AutocompleteStationListPop>
                                )}
                            </div>
                        );
                    }}
                />
                <Autocomplete
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    multiple
                    disableCloseOnSelect
                    className="filter-form-item"
                    options={div_dd ?? []}
                    value={filterForm.div_ids || []}
                    onChange={(_, newValue) => {
                        handleFilterChange("div_ids", newValue);
                    }}
                    getOptionLabel={(opt) => opt.value}
                    renderInput={(params) => <TextField {...params} label="Divisions" />}
                    renderTags={(value, getTagProps) => {
                        return (
                            <div>
                                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                                {value.length > 1 && (
                                    <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                                        <Chip size="small" label={`+${value.length - 1}`} />
                                    </AutocompleteStationListPop>
                                )}
                            </div>
                        );
                    }}
                />
                <Autocomplete
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    multiple
                    disableCloseOnSelect
                    className="filter-form-item"
                    options={customer_dd ?? []}
                    value={filterForm.customer_ids || []}
                    onChange={(_, newValue) => {
                        handleFilterChange("customer_ids", newValue);
                    }}
                    getOptionLabel={(opt) => opt.value}
                    renderInput={(params) => <TextField {...params} label="Stations" />}
                    renderTags={(value, getTagProps) => {
                        return (
                            <div>
                                <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                                {value.length > 1 && (
                                    <AutocompleteStationListPop title={value.map((option) => option?.value ?? "").toString()}>
                                        <Chip size="small" label={`+${value.length - 1}`} />
                                    </AutocompleteStationListPop>
                                )}
                            </div>
                        );
                    }}
                />
                <Button
                    variant="contained"
                    onClick={() => {
                        setFinalFilters({ ...filterForm });
                    }}
                >
                    Search
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => {
                        const initFilters = getInitFilterFrom();
                        setFilterForm(initFilters);
                        setFinalFilters(initFilters);
                    }}
                >
                    Reset
                </Button>
            </div>
            <div className="tmms-dashboard-card-container">
                <div
                    className="tmms-dashboard-card"
                    onClick={() => setActiveCard(TMMSDashboardCard.AllAssets)}
                    data-active={activeCard === TMMSDashboardCard.AllAssets}
                >
                    <div className="tmms-dashboard-card-header">
                        <p>All <br />Assets</p>
                        <p className="blue-text">(As on {dayjs(allCountsData.timeStamp || '').format("DD-MM-YYYY")})</p>
                    </div>
                    <div className="tmms-dashboard-card-value">
                        <p>{allCountsData.AllAssetTotalCount || 0}</p>
                    </div>
                </div>
                <div
                    className="tmms-dashboard-card"
                    onClick={() => setActiveCard(TMMSDashboardCard.MaintenanceSchedule)}
                    data-active={activeCard === TMMSDashboardCard.MaintenanceSchedule}
                >
                    <div className="tmms-dashboard-card-header">
                        <p>Maintenance Schedule Started</p>
                    </div>
                    <div className="tmms-dashboard-card-value flex alignCenter spaceBetween">
                        <p>{allCountsData.MaintenanceStartedDevices || 0}</p>
                        <span className="blue-text">(As on {dayjs(allCountsData.timeStamp || '').format("DD-MM-YYYY")})</span>
                    </div>
                </div>
                <div
                    className="tmms-dashboard-card"
                    onClick={() => setActiveCard(TMMSDashboardCard.AssetHealthOutsidePermissibleRange)}
                    data-active={activeCard === TMMSDashboardCard.AssetHealthOutsidePermissibleRange}
                >
                    <div className="tmms-dashboard-card-header">
                        <p>Asset Health (Outside Permissible Range)</p>
                    </div>
                    <div className="tmms-dashboard-card-value flex alignCenter spaceBetween">
                        <p>{allCountsData.AssetHeathCriticalCount || 0}</p>
                        <span className="blue-text">(As on {dayjs(allCountsData.timeStamp || '').format("DD-MM-YYYY")})</span>
                    </div>
                </div>
                <div className="tmms-dashboard-card" onClick={() => {
                    dispatch(updateSelectedSidebarKey("SFR"));
                    navigate("/failure-report")
                }}>
                    <div className="tmms-dashboard-card-header">
                        <p>Telecom Incidences</p>
                    </div>
                </div>
            </div>

            <div className="tmms-dashboard-content">
                {activeCard === TMMSDashboardCard.AllAssets && <AssetWidget selectedStations={selectedStations} />}
                {activeCard === TMMSDashboardCard.MaintenanceSchedule && <MaintenanceScheduleWidget selectedStations={selectedStations} />}
                {activeCard === TMMSDashboardCard.AssetHealthOutsidePermissibleRange && <AssetHealthWidget selectedStations={selectedStations} />}
            </div>
        </div>
    );
};

export default TMMSDashboard;
